import { useMemo } from "react";
import { useFlasher } from "./hooks/useFlasher";
import { useTheme } from "./hooks/useTheme";
import { DeviceCard } from "./components/DeviceCard";
import { FirmwareCard } from "./components/FirmwareCard";
import { OptionsCard } from "./components/OptionsCard";
import { FlashPanel } from "./components/FlashPanel";
import { ConsolePanel } from "./components/ConsolePanel";
import { IssueList } from "./components/IssueList";
import { DiagnosisPanel } from "./components/DiagnosisPanel";
import { PartitionTablePanel } from "./components/PartitionTablePanel";
import { AlertIcon, MoonIcon, SunIcon } from "./components/Icons";

/**
 * Served from public/, so the URL is resolved against Vite's base rather than
 * bundled. Keeps the same path working at the site root and in a subdirectory.
 */
const LOGO_URL = `${import.meta.env.BASE_URL}schoolasium-logo-square.png`;

export default function App() {
  const flasher = useFlasher();
  const { theme, toggleTheme } = useTheme();
  const {
    environment,
    connection,
    device,
    parts,
    settings,
    flashState,
    progress,
    diagnosis,
    logs,
    monitorText,
    monitorActive,
    openMonitorAfterFlash,
    isReadingFiles,
    assumedChipKey,
    report,
    canFlash,
    hasPort,
  } = flasher;

  // The first uploaded partition table drives the detail panel.
  const partitionSource = useMemo(
    () => parts.find((part) => part.include && part.analysis.partitions !== null),
    [parts],
  );

  const includedCount = parts.filter((part) => part.include).length;

  return (
    <div className="app">
      <header className="masthead">
        <div className="brand">
          {/* DS brand rule: the logo is never recolored, shadowed, stretched, or rotated. */}
          <img className="brand__logo" src={LOGO_URL} alt="Schoolasium" width={44} height={44} />
          <div>
            <h1 className="brand__title">ESP Web Flasher</h1>
            <p className="brand__subtitle">
              Flash ESP32 firmware from the browser — no drivers, no command line.
            </p>
          </div>
        </div>

        <div className="masthead__actions">
          <span className={`pill ${environment.supported ? "pill--success" : "pill--danger"}`}>
            <span className="dot" />
            {environment.supported ? `Web Serial ready — ${environment.browserName}` : "Web Serial unavailable"}
          </span>

          <button
            className="btn theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            {theme === "dark" ? <SunIcon size={16} /> : <MoonIcon size={16} />}
          </button>
        </div>
      </header>

      {!environment.supported && (
        <div className="notice notice--error" style={{ marginBottom: 20 }}>
          <span className="notice__icon">
            <AlertIcon size={18} />
          </span>
          <div className="notice__body">
            <div className="notice__title">{environment.headline}</div>
            <ol className="fixes" style={{ marginTop: 8 }}>
              {environment.guidance.map((line) => (
                <li key={line}>
                  <span>{line}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {diagnosis && (
        <div style={{ marginBottom: 20 }}>
          <DiagnosisPanel diagnosis={diagnosis} onDismiss={flasher.dismissDiagnosis} />
        </div>
      )}

      <main className="workspace">
        <div className="column">
          <DeviceCard
            connection={connection}
            device={device}
            monitorActive={monitorActive}
            assumedChipKey={assumedChipKey}
            supported={environment.supported}
            hasPort={hasPort}
            onSelectAssumedChip={flasher.setAssumedChipKey}
            onConnect={flasher.connect}
            onDisconnect={flasher.disconnect}
            onReset={flasher.resetBoard}
          />

          <FirmwareCard
            parts={parts}
            report={report}
            isReading={isReadingFiles}
            onAddFiles={flasher.addFiles}
            onDropTransfer={flasher.addFromDataTransfer}
            onRemove={flasher.removePart}
            onClear={flasher.clearParts}
            onOffsetChange={flasher.setPartOffset}
            onOffsetReset={flasher.resetPartOffset}
            onToggle={flasher.togglePart}
          />

          <IssueList report={report} hasParts={includedCount > 0} />

          <OptionsCard
            settings={settings}
            openMonitorAfterFlash={openMonitorAfterFlash}
            disabled={flashState === "flashing"}
            onChange={flasher.setSettings}
            onOpenMonitorChange={flasher.setOpenMonitorAfterFlash}
          />

          <FlashPanel
            flashState={flashState}
            progress={progress}
            canFlash={canFlash}
            connected={connection === "connected"}
            partCount={includedCount}
            totalBytes={report.totalBytes}
            blocked={report.hasErrors}
            onFlash={flasher.flash}
          />
        </div>

        <div className="column">
          <ConsolePanel
            logs={logs}
            monitorText={monitorText}
            monitorActive={monitorActive}
            canMonitor={hasPort}
            onStartMonitor={flasher.startMonitor}
            onStopMonitor={flasher.stopMonitor}
            onClearLogs={flasher.clearLogs}
            onClearMonitor={flasher.clearMonitor}
          />

          {partitionSource?.analysis.partitions && (
            <PartitionTablePanel
              partitions={partitionSource.analysis.partitions}
              sourceFileName={partitionSource.fileName}
            />
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="footer__brand">
          <img className="footer__logo" src={LOGO_URL} alt="" aria-hidden="true" width={20} height={20} />
          <span>Schoolasium &middot; built for Blox</span>
        </div>
        <p>
          Powered by{" "}
          <a href="https://github.com/espressif/esptool-js" target="_blank" rel="noreferrer">
            esptool-js
          </a>{" "}
          and Web Serial. Firmware is read in your browser and sent straight to the board — nothing is uploaded
          anywhere.
        </p>
      </footer>
    </div>
  );
}
